'use client';

import { motion } from 'framer-motion';
import { usePostHog } from 'posthog-js/react';
import { Button } from '@/components/shared/Button';
import { ExclamationDecor } from '@/components/shared/ExclamationDecor';
import { addUTMs } from '@/lib/utils';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import type { HeroClubContent, GlobalContent } from '@/lib/content-schema';

const HERO_GRADIENT = 'bg-gradient-to-b from-zibarit-bone via-zibarit-marron-secundario/30 to-zibarit-verde-secundario/40';

interface Props {
  content: HeroClubContent;
  global: GlobalContent;
}

export function HeroClub({ content, global: g }: Props) {
  const posthog = usePostHog();
  const ctaHref = addUTMs(g.appUrl + '/register', 'club_hero');

  return (
    <section className={`relative min-h-screen flex flex-col justify-center ${HERO_GRADIENT} overflow-hidden`}>
      <ExclamationDecor variant="light-bg" />

      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 text-center"
      >
        <motion.h1 variants={fadeInUp} className="mb-8">
          <span className="text-title text-4xl md:text-6xl lg:text-7xl text-zibarit-negro block">
            {content.titleBefore}
            <span className="font-playfair italic text-zibarit-verde-oscuro">{content.titleAccent1}</span>
            {content.titleBetween}
            <span className="font-playfair italic text-zibarit-marron-oscuro">{content.titleAccent2}</span>
          </span>
        </motion.h1>

        <motion.p
          variants={fadeInUp}
          className="text-body text-base md:text-lg text-zibarit-gris-oscuro max-w-xl mx-auto mb-10"
        >
          {content.subtitle}
        </motion.p>

        {content.ctaVisible && (
          <motion.div variants={fadeInUp}>
            <Button
              variant="dark"
              href={ctaHref}
              magnetic
              size="lg"
              data-ph-capture
              onClick={() => posthog?.capture('landing_cta_clicked', {
                button_text: content.ctaText,
                destination_url: ctaHref,
                page: 'club',
              })}
            >
              {content.ctaText}
            </Button>
          </motion.div>
        )}
      </motion.div>
    </section>
  );
}
